import type { Vector3 } from 'three';
import { vector3 } from '@/domain/geometry/vector3';

export const ARC_SEGMENTS = 32;

export type ArcSpec = {
    center: Vector3;
    radius: number;
    startAngle: number;
    endAngle: number;
};

/**
 * Tessellate a circular arc into line-segment pairs.
 * Returns an array of Vector3 positions suitable for BufferGeometry line segments
 * (pairs: [start0, end0, start1, end1, ...]).
 */
export function tessellateArc(arc: ArcSpec, segments: number = ARC_SEGMENTS): Vector3[] {
    const { center, radius, startAngle, endAngle } = arc;
    if (Math.abs(endAngle - startAngle) < 1e-10) return [];
    const vertices: Vector3[] = [];
    const step = (endAngle - startAngle) / segments;
    for (let i = 0; i < segments; i++) {
        const a0 = startAngle + step * i;
        const a1 = startAngle + step * (i + 1);
        vertices.push(
            vector3(center.x + radius * Math.cos(a0), center.y + radius * Math.sin(a0), 0),
            vector3(center.x + radius * Math.cos(a1), center.y + radius * Math.sin(a1), 0),
        );
    }
    return vertices;
}

/**
 * Tessellate a quadratic Bezier curve into line-segment pairs.
 */
export function tessellateBezier(
    start: Vector3,
    control: Vector3,
    end: Vector3,
    segments: number = ARC_SEGMENTS,
): Vector3[] {
    if (segments < 1) return [];
    const vertices: Vector3[] = [];
    for (let i = 0; i < segments; i++) {
        const t0 = i / segments;
        const t1 = (i + 1) / segments;
        vertices.push(bezierPoint(start, control, end, t0), bezierPoint(start, control, end, t1));
    }
    return vertices;
}

function bezierPoint(start: Vector3, control: Vector3, end: Vector3, t: number): Vector3 {
    const s = 1 - t;
    return vector3(
        s * s * start.x + 2 * s * t * control.x + t * t * end.x,
        s * s * start.y + 2 * s * t * control.y + t * t * end.y,
        0,
    );
}

/**
 * Tessellate a dashed line into line-segment pairs with gaps.
 */
export function tessellateDashedLine(
    start: Vector3,
    end: Vector3,
    dashLength: number,
    gapLength: number,
): Vector3[] {
    const dir = end.clone().sub(start);
    const totalLength = dir.length();
    if (totalLength < 1e-10) return [];
    const unit = dir.normalize();

    const vertices: Vector3[] = [];
    let offset = 0;
    while (offset < totalLength) {
        const dashEnd = Math.min(offset + dashLength, totalLength);
        vertices.push(
            vector3(
                start.x + unit.x * offset,
                start.y + unit.y * offset,
                0,
            ),
            vector3(
                start.x + unit.x * dashEnd,
                start.y + unit.y * dashEnd,
                0,
            ),
        );
        offset = dashEnd + gapLength;
    }
    return vertices;
}

export type DashedCircleSpec = {
    center: Vector3;
    radius: number;
    dashLength: number;
    gapLength: number;
};

/**
 * Tessellate a circle into dashed line-segment pairs.
 * Returns an array of Vector3 positions suitable for BufferGeometry line segments
 * (pairs: [start0, end0, start1, end1, ...]).
 *
 * The circle is approximated by `segments` chord points (default 64). Dashes
 * advance along the perimeter using `dashLength` and `gapLength` measured in
 * world units along the perimeter.
 */
export function tessellateDashedCircle(circle: DashedCircleSpec, segments: number = 64): Vector3[] {
    const { center, radius, dashLength, gapLength } = circle;
    if (radius <= 0) return [];
    const circumference = 2 * Math.PI * radius;
    if (circumference < 1e-10) return [];

    const vertices: Vector3[] = [];
    let s = 0;
    while (s < circumference) {
        const dashEnd = Math.min(s + dashLength, circumference);
        const a0 = s / radius;
        const a1 = dashEnd / radius;
        // Walk from a0 to a1 in small steps so the dash hugs the circle
        const stepCount = Math.max(1, Math.ceil(((a1 - a0) / (2 * Math.PI)) * segments));
        const step = (a1 - a0) / stepCount;
        for (let i = 0; i < stepCount; i++) {
            const t0 = a0 + step * i;
            const t1 = a0 + step * (i + 1);
            vertices.push(
                vector3(center.x + radius * Math.cos(t0), center.y + radius * Math.sin(t0), center.z),
                vector3(center.x + radius * Math.cos(t1), center.y + radius * Math.sin(t1), center.z),
            );
        }
        s = dashEnd + gapLength;
    }
    return vertices;
}
